import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
    Box, Card, Typography, TextField, Button,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Alert, CircularProgress, Paper, Chip,
    Dialog, DialogTitle, DialogContent, DialogActions, Divider
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    PlayArrow as PlayArrowIcon,
    Undo as UndoIcon,
    Visibility as PreviewIcon
} from '@mui/icons-material';
import { API_BASE } from '../config';

interface ShiftPreview {
    matchedUsers: number;
    uniqueAffectedEmails: number;
    normalCount: number;
    platinumCount: number;
    totalCount: number;
    newIncomeDate: string;
    affectedEmails: string[];
    normalSample: any[];
    platinumSample: any[];
}

interface ShiftOperation {
    operationId: string;
    totalCount: number;
    shiftedCount: number;
    rolledBackCount: number;
    normalCount: number;
    platinumCount: number;
    newIncomeDate: string;
    shiftedAt: string;
    metadata: any;
    uniqueEmailCount: number;
    status: 'SHIFTED' | 'ROLLED_BACK' | 'PARTIAL_ROLLBACK';
}

interface RollbackPreview {
    count: number;
    normalCount: number;
    platinumCount: number;
    sample: any[];
}

const IncomeDateShift: React.FC = () => {
    // Config state (pre-filled defaults)
    const [signupFrom, setSignupFrom] = useState('2025-12-01');
    const [signupTo, setSignupTo] = useState('2026-03-24');
    const [investFrom, setInvestFrom] = useState('2025-12-01');
    const [investTo, setInvestTo] = useState('2026-03-24');
    const [newIncomeDate, setNewIncomeDate] = useState('2026-04-24');

    // Preview state
    const [preview, setPreview] = useState<ShiftPreview | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);

    // Execute state
    const [executeLoading, setExecuteLoading] = useState(false);
    const [confirmExecute, setConfirmExecute] = useState(false);
    const [executeResult, setExecuteResult] = useState<{ operationId: string; message: string } | null>(null);

    // Operations history state
    const [operations, setOperations] = useState<ShiftOperation[]>([]);
    const [opsLoading, setOpsLoading] = useState(false);

    // Rollback state
    const [rollbackTarget, setRollbackTarget] = useState<string | null>(null);
    const [rollbackPreview, setRollbackPreview] = useState<RollbackPreview | null>(null);
    const [rollbackPreviewLoading, setRollbackPreviewLoading] = useState(false);
    const [rollbackLoading, setRollbackLoading] = useState(false);
    const [confirmRollback, setConfirmRollback] = useState(false);

    // UI state
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const getToken = () => localStorage.getItem('token');

    const fetchOperations = useCallback(async () => {
        setOpsLoading(true);
        try {
            const res = await axios.get(`${API_BASE}/api/income-date-shift/operations`, {
                headers: { Authorization: `Bearer ${getToken()}` }
            });
            if (res.data.status === 'success') {
                setOperations(res.data.data || []);
            }
        } catch (err: any) {
            console.error('Failed to fetch operations:', err);
        } finally {
            setOpsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchOperations();
    }, [fetchOperations]);

    const handlePreview = async () => {
        setPreviewLoading(true);
        setError(null);
        setPreview(null);
        try {
            const res = await axios.post(`${API_BASE}/api/income-date-shift/shift`, {
                signupFrom, signupTo, investFrom, investTo, newIncomeDate, dryRun: true
            }, { headers: { Authorization: `Bearer ${getToken()}` } });

            if (res.data.status === 'success' && res.data.dryRun) {
                setPreview(res.data.data);
            } else if (res.data.data?.matchedUsers === 0) {
                setError('No users found in the signup date range.');
            } else {
                setError(res.data.message || 'Unexpected response.');
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to fetch preview.');
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleExecute = async () => {
        setConfirmExecute(false);
        setExecuteLoading(true);
        setError(null);
        setExecuteResult(null);
        try {
            const res = await axios.post(`${API_BASE}/api/income-date-shift/shift`, {
                signupFrom, signupTo, investFrom, investTo, newIncomeDate, dryRun: false
            }, { headers: { Authorization: `Bearer ${getToken()}` } });

            if (res.data.status === 'success') {
                setExecuteResult({
                    operationId: res.data.data?.operationId || 'unknown',
                    message: res.data.message || 'Shift initiated successfully.'
                });
                setSuccess('Income date shift executed successfully. Emails populated with 1% rate and 30-day cycle.');
                setTimeout(() => setSuccess(null), 8000);
                setPreview(null);
                fetchOperations();
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to execute shift.');
        } finally {
            setExecuteLoading(false);
        }
    };

    const handleRollbackPreview = async (operationId: string) => {
        setRollbackTarget(operationId);
        setRollbackPreviewLoading(true);
        setRollbackPreview(null);
        try {
            const res = await axios.post(`${API_BASE}/api/income-date-shift/rollback`, {
                operationId, dryRun: true
            }, { headers: { Authorization: `Bearer ${getToken()}` } });

            if (res.data.status === 'success' && res.data.dryRun) {
                setRollbackPreview(res.data.data);
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to preview rollback.');
            setRollbackTarget(null);
        } finally {
            setRollbackPreviewLoading(false);
        }
    };

    const handleRollbackExecute = async () => {
        if (!rollbackTarget) return;
        setConfirmRollback(false);
        setRollbackLoading(true);
        try {
            const res = await axios.post(`${API_BASE}/api/income-date-shift/rollback`, {
                operationId: rollbackTarget, dryRun: false
            }, { headers: { Authorization: `Bearer ${getToken()}` } });

            if (res.data.status === 'success') {
                setSuccess(`Rollback initiated for operation ${rollbackTarget.slice(0, 8)}...`);
                setTimeout(() => setSuccess(null), 5000);
                setRollbackTarget(null);
                setRollbackPreview(null);
                fetchOperations();
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to execute rollback.');
        } finally {
            setRollbackLoading(false);
        }
    };

    return (
        <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            {/* Header */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, mb: 3, gap: 2 }}>
                <Box>
                    <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, mb: 0.5 }}>Income Date Shift</Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
                        Shift incomeDate for users (signup + investment in date range). Auto-populates email targets with 1% rate & 30-day cycle.
                    </Typography>
                </Box>
                <Button startIcon={<RefreshIcon />} onClick={fetchOperations} variant="contained" disabled={opsLoading} size="small" sx={{ borderRadius: 2 }}>
                    Refresh
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>{success}</Alert>}

            {/* Section 1: Configuration */}
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', p: { xs: 2, sm: 3 }, mb: 4 }}>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>Date Range Configuration</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Users must have <strong>signed up</strong> AND <strong>invested</strong> within the configured ranges. Both boundary dates are <strong>inclusive</strong>.
                </Typography>

                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Signup Date Range</Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                    <TextField label="Signup From" type="date" size="small" value={signupFrom} onChange={(e) => setSignupFrom(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 200 }} />
                    <TextField label="Signup To" type="date" size="small" value={signupTo} onChange={(e) => setSignupTo(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 200 }} />
                </Box>

                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Investment Date Range</Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                    <TextField label="Invest From" type="date" size="small" value={investFrom} onChange={(e) => setInvestFrom(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 200 }} />
                    <TextField label="Invest To" type="date" size="small" value={investTo} onChange={(e) => setInvestTo(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 200 }} />
                </Box>

                <Divider sx={{ mb: 3 }} />

                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Target Income Date</Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3, alignItems: 'center' }}>
                    <TextField label="New Income Date" type="date" size="small" value={newIncomeDate} onChange={(e) => setNewIncomeDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 200 }} />
                    <Chip label="1% Interest Rate" color="primary" size="small" variant="outlined" />
                    <Chip label="30-day Cycle" color="secondary" size="small" variant="outlined" />
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    After execution, <strong>cron_email_targets</strong> will be auto-populated with affected emails, <strong>1% rate</strong> (Normal & Platinum), and <strong>30-day income cycle</strong>.
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button
                        variant="outlined" size="small"
                        startIcon={previewLoading ? <CircularProgress size={18} color="inherit" /> : <PreviewIcon />}
                        onClick={handlePreview}
                        disabled={previewLoading || !signupFrom || !signupTo || !investFrom || !investTo || !newIncomeDate}
                    >
                        Preview Eligible Users
                    </Button>
                    <Button
                        variant="contained" size="small" color="warning"
                        startIcon={executeLoading ? <CircularProgress size={18} color="inherit" /> : <PlayArrowIcon />}
                        onClick={() => setConfirmExecute(true)}
                        disabled={executeLoading || !preview || preview.totalCount === 0}
                    >
                        Execute Shift
                    </Button>
                </Box>
            </Card>

            {/* Preview Results */}
            {preview && (
                <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'primary.main', p: { xs: 2, sm: 3 }, mb: 4 }}>
                    <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>Preview Results (Dry Run)</Typography>

                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 3 }}>
                        <Paper variant="outlined" sx={{ p: 2, minWidth: 140, textAlign: 'center', borderRadius: 2 }}>
                            <Typography variant="h4" fontWeight={700} color="primary">{preview.matchedUsers}</Typography>
                            <Typography variant="caption" color="text.secondary">Users in Signup Range</Typography>
                        </Paper>
                        <Paper variant="outlined" sx={{ p: 2, minWidth: 140, textAlign: 'center', borderRadius: 2 }}>
                            <Typography variant="h4" fontWeight={700} color="success.main">{preview.uniqueAffectedEmails}</Typography>
                            <Typography variant="caption" color="text.secondary">Affected Emails</Typography>
                        </Paper>
                        <Paper variant="outlined" sx={{ p: 2, minWidth: 140, textAlign: 'center', borderRadius: 2 }}>
                            <Typography variant="h4" fontWeight={700}>{preview.normalCount}</Typography>
                            <Typography variant="caption" color="text.secondary">Normal Investments</Typography>
                        </Paper>
                        <Paper variant="outlined" sx={{ p: 2, minWidth: 140, textAlign: 'center', borderRadius: 2 }}>
                            <Typography variant="h4" fontWeight={700}>{preview.platinumCount}</Typography>
                            <Typography variant="caption" color="text.secondary">Platinum Investments</Typography>
                        </Paper>
                        <Paper variant="outlined" sx={{ p: 2, minWidth: 140, textAlign: 'center', borderRadius: 2, bgcolor: 'warning.50' }}>
                            <Typography variant="h4" fontWeight={700} color="warning.dark">{preview.totalCount}</Typography>
                            <Typography variant="caption" color="text.secondary">Total to Shift</Typography>
                        </Paper>
                    </Box>

                    {preview.affectedEmails.length > 0 && (
                        <>
                            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Affected Emails ({preview.affectedEmails.length})</Typography>
                            <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto', p: 1.5, mb: 2, borderRadius: 2 }}>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {preview.affectedEmails.map((email, i) => (
                                        <Chip key={i} label={email} size="small" variant="outlined" />
                                    ))}
                                </Box>
                            </Paper>
                        </>
                    )}

                    {preview.normalSample.length > 0 && (
                        <>
                            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Normal Investment Sample</Typography>
                            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                                            <TableCell><Typography fontWeight={700} variant="caption">Email</Typography></TableCell>
                                            <TableCell><Typography fontWeight={700} variant="caption">Amount</Typography></TableCell>
                                            <TableCell><Typography fontWeight={700} variant="caption">Current Income Date</Typography></TableCell>
                                            <TableCell><Typography fontWeight={700} variant="caption">Created At</Typography></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {preview.normalSample.slice(0, 20).map((inv: any, i: number) => (
                                            <TableRow key={i}>
                                                <TableCell><Typography variant="caption">{inv.email}</Typography></TableCell>
                                                <TableCell><Typography variant="caption">${inv.totalAmount}</Typography></TableCell>
                                                <TableCell><Typography variant="caption">{inv.incomeDate ? new Date(inv.incomeDate).toLocaleDateString() : '-'}</Typography></TableCell>
                                                <TableCell><Typography variant="caption">{new Date(inv.createdAt).toLocaleDateString()}</Typography></TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    )}

                    {preview.platinumSample.length > 0 && (
                        <>
                            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Platinum Investment Sample</Typography>
                            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                                            <TableCell><Typography fontWeight={700} variant="caption">Email</Typography></TableCell>
                                            <TableCell><Typography fontWeight={700} variant="caption">Amount</Typography></TableCell>
                                            <TableCell><Typography fontWeight={700} variant="caption">Current Income Date</Typography></TableCell>
                                            <TableCell><Typography fontWeight={700} variant="caption">Created At</Typography></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {preview.platinumSample.slice(0, 20).map((inv: any, i: number) => (
                                            <TableRow key={i}>
                                                <TableCell><Typography variant="caption">{inv.email}</Typography></TableCell>
                                                <TableCell><Typography variant="caption">${inv.totalAmount}</Typography></TableCell>
                                                <TableCell><Typography variant="caption">{inv.incomeDate ? new Date(inv.incomeDate).toLocaleDateString() : '-'}</Typography></TableCell>
                                                <TableCell><Typography variant="caption">{new Date(inv.createdAt).toLocaleDateString()}</Typography></TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    )}
                </Card>
            )}

            {/* Execute Result */}
            {executeResult && (
                <Alert severity="info" sx={{ mb: 4 }} onClose={() => setExecuteResult(null)}>
                    <Typography variant="body2" fontWeight={600}>{executeResult.message}</Typography>
                    <Typography variant="caption" color="text.secondary">Operation ID: {executeResult.operationId}</Typography>
                </Alert>
            )}

            {/* Section 3: Past Operations & Rollback */}
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
                <Box sx={{ p: { xs: 2, sm: 3 }, pb: 0 }}>
                    <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>Past Operations</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        History of all income date shift operations. You can rollback any active (SHIFTED) operation.
                    </Typography>
                </Box>

                <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table sx={{ minWidth: 700 }}>
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'grey.50' }}>
                                <TableCell><Typography fontWeight={700} variant="body2">Operation ID</Typography></TableCell>
                                <TableCell><Typography fontWeight={700} variant="body2">Date</Typography></TableCell>
                                <TableCell><Typography fontWeight={700} variant="body2">New Income Date</Typography></TableCell>
                                <TableCell align="center"><Typography fontWeight={700} variant="body2">Normal</Typography></TableCell>
                                <TableCell align="center"><Typography fontWeight={700} variant="body2">Platinum</Typography></TableCell>
                                <TableCell align="center"><Typography fontWeight={700} variant="body2">Emails</Typography></TableCell>
                                <TableCell><Typography fontWeight={700} variant="body2">Status</Typography></TableCell>
                                <TableCell align="right"><Typography fontWeight={700} variant="body2">Actions</Typography></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {opsLoading ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                                        <CircularProgress size={30} />
                                    </TableCell>
                                </TableRow>
                            ) : operations.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                                        <Typography color="text.secondary">No shift operations found.</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : operations.map((op) => (
                                <TableRow key={op.operationId} sx={{ opacity: op.status === 'ROLLED_BACK' ? 0.5 : 1 }}>
                                    <TableCell>
                                        <Typography variant="caption" fontFamily="monospace">{op.operationId.slice(0, 8)}...</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{new Date(op.shiftedAt).toLocaleDateString()}</Typography>
                                        <Typography variant="caption" color="text.secondary">{new Date(op.shiftedAt).toLocaleTimeString()}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{new Date(op.newIncomeDate).toLocaleDateString()}</Typography>
                                    </TableCell>
                                    <TableCell align="center"><Typography variant="body2">{op.normalCount}</Typography></TableCell>
                                    <TableCell align="center"><Typography variant="body2">{op.platinumCount}</Typography></TableCell>
                                    <TableCell align="center"><Typography variant="body2">{op.uniqueEmailCount}</Typography></TableCell>
                                    <TableCell>
                                        <Chip
                                            label={op.status}
                                            size="small"
                                            color={op.status === 'SHIFTED' ? 'success' : op.status === 'ROLLED_BACK' ? 'default' : 'warning'}
                                            variant={op.status === 'ROLLED_BACK' ? 'outlined' : 'filled'}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        {op.status === 'SHIFTED' && (
                                            <Button
                                                size="small" variant="outlined" color="error"
                                                startIcon={<UndoIcon />}
                                                onClick={() => handleRollbackPreview(op.operationId)}
                                                disabled={rollbackPreviewLoading}
                                            >
                                                Rollback
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Card>

            {/* Confirm Execute Dialog */}
            <Dialog open={confirmExecute} onClose={() => setConfirmExecute(false)}>
                <DialogTitle sx={{ fontWeight: 700 }}>Confirm Income Date Shift</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        This is a <strong>production operation</strong>. It will modify investment records in the database.
                    </Alert>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        This will shift <strong>{preview?.totalCount || 0} investments</strong> ({preview?.normalCount || 0} Normal + {preview?.platinumCount || 0} Platinum) for <strong>{preview?.uniqueAffectedEmails || 0} users</strong> to income date <strong>{newIncomeDate}</strong>.
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        After execution:
                    </Typography>
                    <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                        <li>All matching investment <code>incomeDate</code> fields will be set to <strong>{newIncomeDate}</strong></li>
                        <li><code>cron_email_targets</code> will be populated with affected emails</li>
                        <li>Interest rate set to <strong>1%</strong> (Normal & Platinum)</li>
                        <li>Income cycle set to <strong>30 days</strong></li>
                        <li>A rollback operation will be available if needed</li>
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmExecute(false)}>Cancel</Button>
                    <Button onClick={handleExecute} color="warning" variant="contained">Execute Shift</Button>
                </DialogActions>
            </Dialog>

            {/* Rollback Dialog */}
            <Dialog open={!!rollbackTarget} onClose={() => { setRollbackTarget(null); setRollbackPreview(null); }} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>Rollback Operation</DialogTitle>
                <DialogContent>
                    {rollbackPreviewLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : rollbackPreview ? (
                        <>
                            <Alert severity="info" sx={{ mb: 2 }}>
                                This will restore the <strong>original incomeDate</strong> for {rollbackPreview.count} investment records.
                            </Alert>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                Records to rollback: <strong>{rollbackPreview.count}</strong> ({rollbackPreview.normalCount} Normal + {rollbackPreview.platinumCount} Platinum)
                            </Typography>
                            {rollbackPreview.sample.length > 0 && (
                                <TableContainer component={Paper} variant="outlined" sx={{ mt: 2, borderRadius: 2, maxHeight: 200 }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ bgcolor: 'grey.50' }}>
                                                <TableCell><Typography fontWeight={700} variant="caption">Email</Typography></TableCell>
                                                <TableCell><Typography fontWeight={700} variant="caption">Type</Typography></TableCell>
                                                <TableCell><Typography fontWeight={700} variant="caption">Old Date</Typography></TableCell>
                                                <TableCell><Typography fontWeight={700} variant="caption">Current Date</Typography></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {rollbackPreview.sample.slice(0, 20).map((s: any, i: number) => (
                                                <TableRow key={i}>
                                                    <TableCell><Typography variant="caption">{s.email}</Typography></TableCell>
                                                    <TableCell><Chip label={s.investType} size="small" variant="outlined" /></TableCell>
                                                    <TableCell><Typography variant="caption">{new Date(s.oldIncomeDate).toLocaleDateString()}</Typography></TableCell>
                                                    <TableCell><Typography variant="caption">{new Date(s.newIncomeDate).toLocaleDateString()}</Typography></TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </>
                    ) : (
                        <Typography color="text.secondary">No records to rollback.</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setRollbackTarget(null); setRollbackPreview(null); }}>Cancel</Button>
                    {rollbackPreview && rollbackPreview.count > 0 && !confirmRollback && (
                        <Button onClick={() => setConfirmRollback(true)} color="error" variant="outlined">
                            Confirm Rollback
                        </Button>
                    )}
                    {confirmRollback && (
                        <Button
                            onClick={handleRollbackExecute} color="error" variant="contained"
                            disabled={rollbackLoading}
                            startIcon={rollbackLoading ? <CircularProgress size={18} color="inherit" /> : <UndoIcon />}
                        >
                            Execute Rollback
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default IncomeDateShift;
